import React, { useState } from 'react';
import { ModalContent } from '../../../v1/components/modals/layout';
import { useSelector } from 'react-redux';
import { PDFViewer } from '@react-pdf/renderer';
import { generateShortlistsFieldMapper } from 'v1/helpers/shortlistFieldHelper';

import { get, mapValues } from 'lodash';
import { maybe, rd } from '@passionware/monads';
import { Grid, GridCell } from 'shared';
import ShortlistFieldManager from 'v1/components/feature/ShortlistComponents/ShortlistPresentView/ShortlistFieldManager/ShortlistFieldManager';
import Modal from 'modals/layout/Modal';
import ResourcePDFDocument from './ResourcePDFDocument';
import { isImageFromType } from 'lib/media/files';
import ModalHeader from 'modals/layout/ModalHeader/ModalHeader';
import { useTranslation } from 'react-i18next';
import {
  AppScopeIdentity,
  appScopeUtils
} from 'v5/core/app-scope/appScope.types';
import { myServices } from 'v5/services/services.connected.app';

const DEFAULT_FIELD_LIST = ['locations', 'emails', 'phone_numbers', 'websites'];

type GenerateResourcePDFModalProps = {
  resourceId: number;
  /**
   * Browsing scope from the opener (inside nested AppScopeProvider).
   * Modal Portal sits outside that tree — do not re-read app scope here.
   */
  browsingScope: AppScopeIdentity;
};

const GenerateResourcePDFModal = ({
  resourceId,
  browsingScope
}: GenerateResourcePDFModalProps) => {
  const resource = useSelector((state: any) => state.contacts.data[resourceId]);
  const associations = useSelector(
    (state: any) => state.contacts.associations_data[resourceId]
  );
  const [selectedFields, setSelectedFields] = useState(DEFAULT_FIELD_LIST);

  const { t } = useTranslation('v1-modals');

  const auth = useSelector((state: any) => state.auth);
  const customfieldDefinitions = get(auth, [
    'settings',
    'custom_field_definitions'
  ]);

  const browsingWorkspaceId = maybe.getOrNull(
    appScopeUtils.getWorkspaceId(browsingScope)
  );

  const fieldsRequest = {
    type: 'resource' as const,
    subtypeId: resource.resource_type_id!,
    scope: browsingScope
  };
  const fieldsRd =
    myServices.metastructureMappingService.useMetastructure(fieldsRequest);
  const resourceTypeFields = rd.getOrElse(
    rd.useLastWithPlaceholder(fieldsRd),
    []
  );

  const fields = generateShortlistsFieldMapper(
    resourceTypeFields,
    customfieldDefinitions,
    browsingWorkspaceId
  );

  const fieldMappings = mapValues(fields, field => ({
    ...field,
    label: t(`ResourceModal.${field.label}`, { defaultValue: field.label })
  }));

  const pronounsFromOptionId =
    myServices.globalSettingsService.usePronounsForResourceType(
      resource?.pronouns_option_id ?? null,
      resource?.resource_type_id ?? null
    );
  const resolvedPronouns = pronounsFromOptionId;

  // Get Pinboard images
  const pins = get(associations, 'pins', []);
  const pinImages = pins.filter(pin => {
    if (pin.archived) return false;
    if (pin.pin_type === 'file') {
      return isImageFromType(pin.file.mime_type);
    } else {
      return isImageFromType(pin.pin_type);
    }
  });

  return (
    <Modal size="l">
      <ModalHeader title={t('ResourceModal.title')} />
      <ModalContent>
        <Grid fullHeight>
          <GridCell width="1/3">
            <Grid direction="column" fullHeight overflow="auto">
              <GridCell padding="m">
                <ShortlistFieldManager
                  onSubmit={({ default_fields }) =>
                    setSelectedFields(default_fields)
                  }
                  // @ts-expect-error ts migration needed
                  default_fields={selectedFields}
                  shortlist_field_mapper={fieldMappings}
                  query={{}}
                  header={t('ResourceModal.managerTitle')}
                />
              </GridCell>
            </Grid>
          </GridCell>
          <GridCell width="2/3">
            <PDFViewer width="100%" height="100%" style={{ border: 0 }}>
              <ResourcePDFDocument
                resource={resource}
                resourcePronouns={resolvedPronouns}
                fieldMappings={fieldMappings}
                gallery={pinImages}
                selectedFields={selectedFields}
              />
            </PDFViewer>
          </GridCell>
        </Grid>
      </ModalContent>
    </Modal>
  );
};

export default GenerateResourcePDFModal;
